import { useEffect, useRef, useState } from "react";
import {
  getGoldEconomicCalendar,
  type CalendarEvent,
} from "@/lib/economic-calendar.functions";

export interface CalendarState {
  events: CalendarEvent[] | null;
  isLoading: boolean;
  error: string | null;
  fetchedAt: number | null;
}

const REFRESH_MS = 15 * 60 * 1000;
const FAST_POLL_MS = 30 * 1000;
const AWAITING_WINDOW_MS = 20 * 60 * 1000;

function hasAwaitingEvent(events: CalendarEvent[] | null): boolean {
  if (!events) return false;
  const now = Date.now();
  return events.some((e) => {
    if (e.actual) return false;
    const t = Date.parse(e.date);
    if (!Number.isFinite(t)) return false;
    const sinceMs = now - t;
    return sinceMs >= 0 && sinceMs <= AWAITING_WINDOW_MS;
  });
}

// `seed` (phone app) is a reading already delivered by the Lean context
// bundle, so the first request is skipped. Later refreshes are unchanged.
export function useEconomicCalendar(seed?: { events: CalendarEvent[]; fetchedAt: number } | null): CalendarState {
  const [state, setState] = useState<CalendarState>(() => seed
    ? { events: seed.events, isLoading: false, error: null, fetchedAt: seed.fetchedAt }
    : { events: null, isLoading: true, error: null, fetchedAt: null });
  const cancelledRef = useRef(false);
  const loadRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    cancelledRef.current = false;
    const load = async () => {
      try {
        const { events, fetchedAt } = await getGoldEconomicCalendar();
        if (cancelledRef.current) return;
        setState({ events, isLoading: false, error: null, fetchedAt });
      } catch (err) {
        if (cancelledRef.current) return;
        setState((s) => ({
          ...s,
          isLoading: s.events == null,
          error: err instanceof Error ? err.message : "Failed to load calendar",
        }));
      }
    };
    loadRef.current = load;
    if (!seed) load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, []);

  // A newer seed (fresh Lean bundle after a stored one painted) replaces the
  // older reading without an extra request.
  const seedAt = seed?.fetchedAt ?? null;
  useEffect(() => {
    if (!seed || seedAt == null) return;
    setState((s) => (s.fetchedAt != null && s.fetchedAt >= seedAt ? s : { events: seed.events, isLoading: false, error: null, fetchedAt: seedAt }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedAt]);

  // Fast-poll while any event is awaiting an actual value, so the moment the
  // cron enricher writes to the DB the banner picks it up within ~30s.
  const awaiting = hasAwaitingEvent(state.events);
  useEffect(() => {
    if (!awaiting) return;
    const id = setInterval(() => {
      loadRef.current();
    }, FAST_POLL_MS);
    return () => clearInterval(id);
  }, [awaiting]);

  return state;
}
